// Action Card Component
// Date: January 11, 2026
// Purpose: Card view for Action entities

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  UserIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { useLanguage } from '@/state/language/LanguageProvider';
import { Action, ActionStatus, ActionPriority } from '@/services/actionService';

export interface ActionCardProps {
  action: Action;
  onView?: (item: Action) => void;
  onEdit?: (item: Action) => void;
  onDelete?: (item: Action) => void;
  onCopy?: (item: Action) => void;
  onClick?: (id: string) => void;
  canEdit?: (item: Action) => boolean;
  customActions?: Array<{
    label: string | ((item: Action) => string);
    icon: React.ComponentType<any> | ((item: Action) => React.ComponentType<any>);
    onClick: (item: Action) => void | Promise<void>;
    variant?: 'primary' | 'secondary' | 'danger' | ((item: Action) => 'primary' | 'secondary' | 'danger');
  }>;
}

export const ActionCard: React.FC<ActionCardProps> = ({
  action,
  onView,
  onEdit,
  onDelete,
  onCopy,
  onClick,
  canEdit,
  customActions = []
}) => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const handleClick = () => {
    if (onClick) {
      onClick(action.id);
    } else if (onView) {
      onView(action);
    } else {
      navigate(`/actions/${action.id}`);
    }
  };

  const handleActionClick = (e: React.MouseEvent, actionFn: () => void) => {
    e.stopPropagation();
    e.preventDefault();
    actionFn();
  };

  const getPriorityColor = (priority?: ActionPriority) => {
    switch (priority?.toUpperCase()) {
      case 'CRITICAL':
        return 'bg-[var(--color-error)]/20 text-[var(--color-error)] border-[var(--color-error)]/30';
      case 'HIGH':
        return 'bg-[var(--color-warning)]/20 text-[var(--color-warning)] border-[var(--color-warning)]/30';
      case 'MEDIUM':
        return 'bg-[var(--color-warning)]/20 text-[var(--color-warning)] border-[var(--color-warning)]/30';
      case 'LOW':
        return 'bg-[var(--color-success)]/20 text-[var(--color-success)] border-[var(--color-success)]/30';
      default:
        return 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border-[var(--color-border)]';
    }
  };

  const getStatusColor = (status?: ActionStatus) => {
    switch (status?.toUpperCase()) {
      case 'COMPLETED':
        return 'bg-[var(--color-success)]/20 text-[var(--color-success)] border-[var(--color-success)]/30';
      case 'IN_PROGRESS':
        return 'bg-[var(--color-info)]/20 text-[var(--color-info)] border-[var(--color-info)]/30';
      case 'ON_HOLD':
        return 'bg-[var(--color-warning)]/20 text-[var(--color-warning)] border-[var(--color-warning)]/30';
      case 'CANCELLED':
        return 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border-[var(--color-border)]';
      case 'PENDING':
        return 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border-[var(--color-border)]';
      default:
        return 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border-[var(--color-border)]';
    }
  };

  const getStatusIcon = (status?: ActionStatus) => {
    switch (status?.toUpperCase()) {
      case 'COMPLETED':
        return <CheckCircleIcon className="w-5 h-5 text-[var(--color-success)]" />;
      case 'IN_PROGRESS':
        return <ClockIcon className="w-5 h-5 text-[var(--color-info)]" />;
      case 'CANCELLED':
        return <XCircleIcon className="w-5 h-5 text-[var(--color-secondary-text)] opacity-75" />;
      case 'ON_HOLD':
        return <ExclamationTriangleIcon className="w-5 h-5 text-[var(--color-warning)]" />;
      default:
        return <ClockIcon className="w-5 h-5 text-[var(--color-secondary-text)] opacity-75" />;
    }
  };

  return (
    <Card 
      variant="outline" 
      padding="none"
      className="hover:shadow-md transition-shadow cursor-pointer h-full flex flex-col border border-[var(--color-border)] rounded-lg bg-[var(--color-surface-bg)]"
      onClick={handleClick}
    >
      <CardContent className="p-4 flex-1 flex flex-col">
        {/* Top: status */}
        {action.status && (
          <div className="flex items-center gap-2 mb-2">
            {getStatusIcon(action.status)}
            <Badge className={`text-xs border ${getStatusColor(action.status)}`}>
              {action.status.replace('_', ' ')}
            </Badge>
          </div>
        )}
        {/* Title + actions */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-[var(--color-secondary-text)] truncate">
              {action.title}
            </h3>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Action Buttons */}
            <div className="flex items-center gap-1 ml-2" onClick={(e) => e.stopPropagation()}>
              {onView && (
                <button
                  onClick={(e) => handleActionClick(e, () => onView(action))}
                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                  title={t('common.view')}
                  type="button"
                >
                  <EyeIcon className="w-4 h-4" />
                </button>
              )}
              {onEdit && (canEdit ? canEdit(action) : true) && (
                <button
                  onClick={(e) => handleActionClick(e, () => onEdit(action))}
                  className="p-1.5 text-green-600 hover:bg-green-50 rounded-md transition-colors"
                  title={t('common.edit')}
                  type="button"
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
              )}
              {onCopy && (
                <button
                  onClick={(e) => handleActionClick(e, () => onCopy(action))}
                  className="p-1.5 text-purple-600 hover:bg-purple-50 rounded-md transition-colors"
                  title={t('common.copy')}
                  type="button"
                >
                  <DocumentDuplicateIcon className="w-4 h-4" />
                </button>
              )}
              {onDelete && (
                <button
                  onClick={(e) => handleActionClick(e, () => onDelete(action))}
                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                  title={t('common.delete')}
                  type="button"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              )}
              {customActions.map((customAction, index) => {
                const IconComponent = typeof customAction.icon === 'function' && customAction.icon.length > 0
                  ? (customAction.icon as (item: Action) => React.ComponentType<any>)(action)
                  : customAction.icon as React.ComponentType<any>;
                const variant = typeof customAction.variant === 'function' 
                  ? customAction.variant(action) 
                  : customAction.variant || 'primary';
                const colorClass = variant === 'danger' 
                  ? 'text-red-600 hover:bg-red-50' 
                  : variant === 'secondary' 
                  ? 'text-gray-600 hover:bg-gray-50' 
                  : 'text-blue-600 hover:bg-blue-50';
                
                return (
                  <button
                    key={index}
                    onClick={(e) => handleActionClick(e, () => customAction.onClick(action))}
                    className={`p-1.5 ${colorClass} rounded-md transition-colors`}
                    title={typeof customAction.label === 'function' ? customAction.label(action) : customAction.label}
                    type="button"
                  >
                    <IconComponent className="w-4 h-4" />
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Description */}
        {action.description && (
          <p className="text-sm text-[var(--color-secondary-text)] opacity-75 line-clamp-2 mb-3">
            {action.description}
          </p>
        )}

        {/* Bottom: priority (importance), then metadata */}
        <div className="space-y-2 mb-3 flex-1 mt-auto">
          {action.priority && (
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge className={`text-xs border ${getPriorityColor(action.priority)}`}>
                {action.priority}
              </Badge>
            </div>
          )}
          {action.assigned_users && action.assigned_users.length > 0 && (
            <div className="flex items-center gap-2 text-xs text-[var(--color-secondary-text)] opacity-75">
              <UserIcon className="w-4 h-4" />
              <span>
                Assigned to: {action.assigned_users.map(u => u.user_display || u.user_name || 'Unknown').join(', ')}
              </span>
            </div>
          )}
          {action.due_date && (
            <div className="flex items-center gap-2 text-xs text-[var(--color-secondary-text)] opacity-75">
              <CalendarIcon className="w-4 h-4" />
              <span>Due: {new Date(action.due_date).toLocaleDateString()}</span>
            </div>
          )}
          {action.assigned_users && action.assigned_users.length > 0 && (
            <div className="flex items-center gap-2 text-xs text-[var(--color-secondary-text)] opacity-75">
              <UserIcon className="w-4 h-4" />
              <span>
                Assigned by: {[...new Set(action.assigned_users.map(u => u.assigned_by_display || u.assigned_by_name || 'Unknown'))].join(', ')}
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

